import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'gatsby';

import CartItem from './CartItem';
import iconArrowLeftAlt from '../../images/icon-arrow-left-alt.svg';
import CurrencySwitcher from '../Header/currencyswitcher/CurrencySwitcher';
import { AppContext, AppContext as CurrencyContext } from '../../apollo/wrap-root-element';
import { useApplyCartDiscountCodes, useCartCount, useCartItems, useCheckout } from '../../context/storeContext';
import { shopifyMarketCountries } from '../../utils/countries';
import SupportSection from './SupportSection';
import { getAppliedCartDiscountCodes, getCartDiscountCode, mapLineItemsWithTierPricing } from '../../utils/discounts';

export default function CartComponent({ opened, onClose, getCart }) {
  const lineItems = useCartItems();
  const cartCount = useCartCount();
  const checkout = useCheckout();
  const applyCartDiscountCodes = useApplyCartDiscountCodes();

  const { currency } = useContext(CurrencyContext);
  const { setShowPopup } = useContext(AppContext);

  const [donationAmount, setDonationAmount] = useState(0);
  const [hasDonated, setHasDonated] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');

  useEffect(() => {
    setIsCheckingOut(false);
    setCheckoutError('');
  }, [opened]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsCheckingOut(false);
        setCheckoutError('');
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Load donation from localStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedDonation = localStorage.getItem('lineage_donation_amount');
      if (savedDonation) {
        const amount = parseFloat(savedDonation);
        setDonationAmount(amount);
        setHasDonated(amount > 0);
      }
    }
  }, []);

  // Save donation to localStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lineage_donation_amount', donationAmount.toString());
    }
  }, [donationAmount]);

  const handleDonationComplete = (amount) => {
    setDonationAmount(amount);
    setHasDonated(true);
  };

  const handleAdjustDonation = (newAmount) => {
    setDonationAmount(newAmount);
    setHasDonated(newAmount > 0);
  };

  const cartItems = mapLineItemsWithTierPricing(!!lineItems ? lineItems : []);

  const calculatedTotal = cartItems.reduce((total, item) => {
    return total + item.price * item.quantity;
  }, 0);

  const originalSubtotal = cartItems.reduce((total, item) => {
    return total + (item.originalPrice || item.price) * item.quantity;
  }, 0);
  const displayTotal = calculatedTotal.toFixed(2);
  const discountAmount = Math.max(0, originalSubtotal - calculatedTotal);
  const appliedDiscountCodes = getAppliedCartDiscountCodes(cartItems);
  const bulkDiscountCode = getCartDiscountCode(cartItems);

  const appendCheckoutParams = (checkoutUrl) => {
    if (!checkoutUrl) return '';

    const separator = checkoutUrl.includes('?') ? '&' : '?';
    const params = new URLSearchParams();

    params.set('currency', currency);

    const countryData = shopifyMarketCountries.find((country) => country.currency === currency);

    if (countryData) {
      params.set('checkout[shipping_address][country]', countryData.code);
      params.set('locale', countryData.locale);
    }

    return `${checkoutUrl}${separator}${params.toString()}`;
  };

  const handleCheckout = async (event) => {
    event.preventDefault();
    if (isCheckingOut || !checkout) return;

    setCheckoutError('');
    setIsCheckingOut(true);

    try {
      let checkoutUrl = checkout;

      if (bulkDiscountCode) {
        const { cart: updatedCart, errors } = await applyCartDiscountCodes([bulkDiscountCode]);

        if (errors?.length) {
          setCheckoutError(
            `Bulk discount code "${bulkDiscountCode}" could not be applied. Ask the shop admin to create this code in Shopify.`
          );
          setIsCheckingOut(false);
          return;
        }

        if (updatedCart?.checkoutUrl) {
          checkoutUrl = updatedCart.checkoutUrl;
        }
      }

      window.location.href = appendCheckoutParams(checkoutUrl);
    } catch (error) {
      setCheckoutError('Checkout failed. Please try again.');
      setIsCheckingOut(false);
    }
  };

  const showCurrencyMessage = currency !== 'USD';

  if (!opened) {
    return <></>;
  }

  return (
    <div className={`popup popup-nav popup-cart ${opened ? 'active' : ''}`}>
      <div className="pd-x-lg nav-top">
        <div className="block d-flex">
          <a className="nav-back" onClick={onClose}>
            <img src={iconArrowLeftAlt} alt="Left Arrow" />
          </a>
          <div className={`nav-bars ${opened ? 'open' : ''}`} onClick={onClose} />
        </div>
      </div>
      <div className="nav-bottom cart">
        <div className="cart-head">
          <div className="cart-title">Shopping Cart</div>
          <div className="cart-item">
            <span className="cart-counter">{cartCount ? `${cartCount} Items` : '0 Item'}</span>
          </div>
        </div>
        <div className="cart-body">
          {!!cartItems?.length &&
            cartItems.map((cartItem, index) => <CartItem item={cartItem} key={`${index}-${cartItem?.id}`} />)}
        </div>
        <SupportSection
          setShowPopup={setShowPopup}
          hasDonated={hasDonated}
          donationAmount={donationAmount}
          onDonationComplete={handleDonationComplete}
          onAdjustDonation={handleAdjustDonation}
        />
        <div className="cart-footer">
          <div className="cart-total">
            <div className="cart-total-row">
              <span className="cart-total-label">Total products</span>
              <span className="cart-total-value">
                <CurrencySwitcher price={originalSubtotal || displayTotal || '-'} caller="cart comp" />
              </span>
            </div>
            {discountAmount > 0.009 && (
              <div className="cart-total-row cart-discount">
                <span className="cart-total-label">
                  Discount
                  {appliedDiscountCodes.length > 0 && (
                    <small className="cart-discount-codes">{appliedDiscountCodes.join(', ')}</small>
                  )}
                </span>
                <span className="cart-total-value">
                  -<CurrencySwitcher price={discountAmount.toFixed(2)} caller="cart discount" />
                </span>
              </div>
            )}
            <div className="cart-total-row">
              <span className="cart-total-label">Shipping</span>
              <span className="cart-total-value cart-shipping-amount">calculated during checkout</span>
            </div>
            <div className="cart-total-row cart-grand-total">
              <span className="cart-total-label">Total</span>
              <span className="cart-total-value">
                <CurrencySwitcher price={displayTotal || '-'} caller="total" />
              </span>
            </div>
          </div>
          {showCurrencyMessage && (
            <div className="currency-note-section">
              <p className="currency-info">
                Lineage Journey process all orders in US dollars. Although your shopping is displaying in {currency} for
                ease of calculation, you will be checked out in US dollars using today's exchange rate.
              </p>
            </div>
          )}
          {!!checkoutError && <p className="cart-checkout-error">{checkoutError}</p>}
          <div className="cart-checkout">
            <Link to="/shop/" className="btn-continue">
              Continue shopping
            </Link>
            {cartCount > 0 && (
              <button
                type="button"
                onClick={handleCheckout}
                disabled={isCheckingOut}
                className="btn btn-md btn-primary-ii btn-checkout"
              >
                {isCheckingOut ? 'Applying discount…' : 'Go To Checkout'}
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="popup-overlay" onClick={onClose}></div>
    </div>
  );
}
